import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { BLANK, DRAFT_KEY, EMPTY, num } from "@/lib/veiviser";

const readDraft = () => { try { const d = JSON.parse(sessionStorage.getItem(DRAFT_KEY) || "null"); return d ? { ...EMPTY, ...d } : EMPTY; } catch { return EMPTY; } };
const vesselBody = (v) => ({ name: v.vessel.name.trim(), type: v.vessel.type.trim() || undefined, loa: num(v.vessel.loa) || undefined, beam: num(v.vessel.beam) || undefined });

/** The wizard's state, its three live answers from the server (catalogue, priced preview, capacity) and the final create. */
export function useVeiviser() {
  const [v, setV] = useState(readDraft);
  const [catalog, setCatalog] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [cap, setCap] = useState(null);
  const [error, setError] = useState(null);
  const lastTemplate = useRef(v.template);

  useEffect(() => { sessionStorage.setItem(DRAFT_KEY, JSON.stringify(v)); }, [v]);
  useEffect(() => { api.get("/veiviser/katalog").then((r) => setCatalog(r.data)).catch((e) => setError(errMsg(e))); }, []);

  const set = useCallback((path, value) => setV((cur) => {
    const [a, b] = path.split(".");
    return b ? { ...cur, [a]: { ...cur[a], [b]: value } } : { ...cur, [a]: value };
  }), []);

  const template = catalog?.templates.find((t) => t.key === v.template) || null;
  const loa = num(v.vessel.loa);
  const beam = num(v.vessel.beam);
  const days = num(v.period.days);

  // The priced standard posts for this hull, place and length of stay – asked again whenever one of them changes.
  useEffect(() => {
    if (!v.template || v.template === BLANK || !v.vessel.name.trim()) { setPreview(null); return undefined; }
    setPreviewing(true);
    const t = setTimeout(() => {
      api.post("/veiviser/forhandsvis", { template: v.template, vessel: vesselBody(v), place: v.period.place, days: days || null })
        .then((r) => {
          setPreview(r.data);
          setV((cur) => {
            const fresh = lastTemplate.current !== cur.template;
            lastTemplate.current = cur.template;
            return {
              ...cur,
              off: fresh ? r.data.items.filter((i) => !i.default).map((i) => i.ref) : cur.off,
              period: { ...cur.period, place: cur.period.place || r.data.place, days: cur.period.days || String(r.data.days) },
            };
          });
        })
        .catch((e) => setError(errMsg(e)))
        .finally(() => setPreviewing(false));
    }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [v.template, v.vessel.name, loa, beam, v.period.place, days]);

  const keys = useMemo(() => (preview ? preview.items.filter((i) => !v.off.includes(i.ref)).map((i) => i.ref) : null), [preview, v.off]);

  // What the plan says about this period: the place and the trades, as if the job were already a case.
  useEffect(() => {
    if (!v.period.from || !(days > 0)) { setCap(null); return undefined; }
    const t = setTimeout(() => {
      api.post("/veiviser/kapasitet", { template: v.template || BLANK, keys, vessel: vesselBody(v), period: { from: v.period.from, days, place: v.period.place } })
        .then((r) => setCap(r.data)).catch(() => setCap(null));
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [v.period.from, days, v.period.place, v.template, keys?.join(","), loa]);

  const totals = useMemo(() => {
    const rows = (preview?.items || []).filter((i) => !v.off.includes(i.ref));
    return { posts: rows.length + v.extra.length, sum: rows.filter((i) => i.in_sum && i.price_type !== "opsjon").reduce((a, i) => a + (i.price || 0), 0), options: rows.filter((i) => i.price_type === "opsjon").reduce((a, i) => a + (i.price || 0), 0), hours: rows.reduce((a, i) => a + (i.hours || 0), 0) };
  }, [preview, v.off, v.extra.length]);

  const create = useCallback(async () => {
    const body = {
      customer: { name: v.customer.name.trim(), account_id: v.customer.account_id || undefined },
      vessel: vesselBody(v), contact: v.contact, template: v.template || BLANK, keys: v.template && v.template !== BLANK ? keys : null,
      extra: v.extra, period: v.period.from ? { from: v.period.from, days, place: v.period.place } : { days: days || undefined, place: v.period.place },
      facts: v.facts, name: v.name.trim() || null, note: v.note.trim() || null, practice: v.practice,
    };
    const r = await api.post("/veiviser", body);
    sessionStorage.removeItem(DRAFT_KEY);
    window.dispatchEvent(new Event("projects:changed"));
    return r.data.project;
  }, [v, keys, days]);

  const reset = useCallback(() => { sessionStorage.removeItem(DRAFT_KEY); lastTemplate.current = null; setV(EMPTY); setPreview(null); setCap(null); }, []);

  return { v, set, setV, catalog, template, preview, previewing, cap, totals, keys, error, create, reset };
}
